import { ApiService } from '../apiService'
import {
  AdventureDto,
  AdventureListResponse,
  AdventureListParams,
  CreateAdventureRequest,
  UpdateAdventureRequest,
  SavedSearchFilterDto,
  CreateSavedSearchFilterRequest,
  AdventureSearchRequest,
  AdventureCategoryDto,
  AdventureJoinRequest,
  AdventureJoinResponse,
  CriteriaTypeDto,
  NodeTypeDto,
} from '../types/adventure'
import { PublicQueueSessionDto, ModeratorStatusDto } from '../types/session'
import { ApiResponse } from '../types/common'
import { AdventureLeaderboardResponse, LeaderboardQueryParams } from '../types/leaderboard'
import {
  AdventureMaintainerDto,
  AdventureModeratorDto,
  AdventureTesterDto,
  AddTeamMemberRequest,
  UnlockRequirement,
} from '@features/adventures/types/adventure-editor.types'

// Criteria request/response types
export interface AddCriterionRequest {
  conditionType: number
  config: Record<string, unknown>
  logicGroupId: string // Required - must belong to a logic group
  order?: number
}

export interface UpdateCriterionRequest {
  conditionType?: number
  config?: Record<string, unknown>
  logicGroupId?: string
  order?: number
}

export interface CriterionResponse {
  id: string
  conditionType: number
  description?: string
  isOptional?: boolean
  config: Record<string, unknown>
  priority: number // API uses "priority" for ordering
}

// Logic Group types - API uses nested response format
export interface CriteriaGroupDto {
  id: string
  isAnd: boolean
  orderIndex: number
  criteria: CriterionResponse[]
}

export interface CriteriaGroupCreateRequest {
  isAnd: boolean
}

export interface CriteriaGroupUpdateRequest {
  isAnd: boolean
}

export interface CriteriaGroupResponse {
  id: string
  isAnd: boolean
  orderIndex: number
}

export const adventuresApi = {
  // Get list of adventures with filters and pagination
  getAdventures: (params?: AdventureListParams) =>
    ApiService.get<AdventureListResponse>('/api/adventures', params),

  // Search adventures with advanced filters
  searchAdventures: (searchRequest: AdventureSearchRequest) =>
    ApiService.post<AdventureListResponse>('/api/adventures/search', searchRequest),

  // Get user's created silent adventures
  getMySilentAdventures: () =>
    ApiService.get<AdventureDto[]>('/api/adventures/my-silent-adventures'),

  // Get single adventure by ID (for viewing)
  getAdventure: (id: string) =>
    ApiService.get<AdventureDto>(`/api/adventures/${id}`),

  // Get adventure for editing (includes all editor fields)
  getAdventureForEdit: (id: string) =>
    ApiService.get<AdventureDto>(`/api/adventures/${id}/edit`),

  // Create new adventure
  createAdventure: (data: CreateAdventureRequest) =>
    ApiService.post<ApiResponse<AdventureDto>>('/api/adventures', data),

  // Update existing adventure
  updateAdventure: (id: string, data: UpdateAdventureRequest) =>
    ApiService.put<ApiResponse<AdventureDto>>(`/api/adventures/${id}`, data),

  // Delete adventure
  deleteAdventure: (id: string) =>
    ApiService.delete<ApiResponse<void>>(`/api/adventures/${id}`),

  // Publish adventure (requires testing status)
  publishAdventure: (id: string) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/workflow/publish`),

  // Join adventure (solo)
  joinAdventure: (id: string) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/join`),

  // Join adventure solo (explicit)
  joinAdventureSolo: (id: string, startDateTime?: string, isTestSession?: boolean) =>
    ApiService.post<AdventureJoinResponse>(`/api/adventures/${id}/join/solo`, { startDateTime, isTestSession }),

  // Create group and join adventure
  createGroupJoin: (id: string, request: Omit<AdventureJoinRequest, 'adventureId'>) =>
    ApiService.post<AdventureJoinResponse>(`/api/adventures/${id}/join/create-group`, request),

  // Join with existing group
  joinWithGroup: (id: string, request: Omit<AdventureJoinRequest, 'adventureId'>) =>
    ApiService.post<AdventureJoinResponse>(`/api/adventures/${id}/join/group`, request),

  // Join public queue
  joinPublic: (id: string, request: Omit<AdventureJoinRequest, 'adventureId'>) =>
    ApiService.post<AdventureJoinResponse>(`/api/adventures/${id}/join/public`, request),

  // Create a new session (invite-only or public)
  createSession: (id: string, request: Omit<AdventureJoinRequest, 'adventureId'>) =>
    ApiService.post<AdventureJoinResponse>(`/api/adventures/${id}/join/create-session`, request),

  // Get public sessions for an adventure
  getPublicSessions: (id: string) =>
    ApiService.get<PublicQueueSessionDto[]>(`/api/adventures/${id}/public-sessions`),

  // Join an existing public session
  joinPublicSession: (sessionId: string) =>
    ApiService.post<AdventureJoinResponse>(`/api/adventures/sessions/${sessionId}/join`),

  // Leave an adventure session
  leaveAdventureSession: (sessionId: string) =>
    ApiService.delete<ApiResponse<void>>(`/api/adventures/sessions/${sessionId}/leave`),

  // Leave a queue
  leaveQueue: (queueId: string) =>
    ApiService.delete<ApiResponse<void>>(`/api/adventures/queues/${queueId}/leave`),

  // Abandon adventure (leave/quit)
  abandonAdventure: (id: string) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/abandon`),

  // Complete adventure
  completeAdventure: (id: string) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/complete`),

  // Save adventure tags
  saveTags: (id: string, tagIds: string[]) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/tags`, tagIds),

  // Team Management - Maintainers
  getMaintainers: (id: string) =>
    ApiService.get<AdventureMaintainerDto[]>(`/api/adventures/${id}/maintainers`),
  addMaintainer: (id: string, data: AddTeamMemberRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/maintainers`, data),
  removeMaintainer: (id: string, userId: string) =>
    ApiService.delete<ApiResponse<void>>(`/api/adventures/${id}/maintainers/${userId}`),

  // Team Management - Moderators
  getModerators: (id: string) =>
    ApiService.get<AdventureModeratorDto[]>(`/api/adventures/${id}/moderators`),
  addModerator: (id: string, data: AddTeamMemberRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/moderators`, data),
  removeModerator: (id: string, userId: string) =>
    ApiService.delete<ApiResponse<void>>(`/api/adventures/${id}/moderators/${userId}`),

  // Team Management - Testers
  getTesters: (id: string) =>
    ApiService.get<AdventureTesterDto[]>(`/api/adventures/${id}/testers`),
  addTester: (id: string, data: AddTeamMemberRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/testers`, data),
  removeTester: (id: string, userId: string) =>
    ApiService.delete<ApiResponse<void>>(`/api/adventures/${id}/testers/${userId}`),

  // Saved Filters Management
  getSavedFilters: () =>
    ApiService.get<SavedSearchFilterDto[]>('/api/Adventures/search/filters'),

  getSavedFilter: (id: string) =>
    ApiService.get<SavedSearchFilterDto>(`/api/Adventures/search/filters/${id}`),

  createSavedFilter: (data: CreateSavedSearchFilterRequest) =>
    ApiService.post<SavedSearchFilterDto>('/api/Adventures/search/filters', data),

  updateSavedFilter: (id: string, data: CreateSavedSearchFilterRequest) =>
    ApiService.put<SavedSearchFilterDto>(`/api/Adventures/search/filters/${id}`, data),

  deleteSavedFilter: (id: string) =>
    ApiService.delete<void>(`/api/Adventures/search/filters/${id}`),

  setDefaultFilter: (id: string) =>
    ApiService.post<void>(`/api/Adventures/search/filters/${id}/set-default`),

  // Get adventure categories
  getCategories: () =>
    ApiService.get<AdventureCategoryDto[]>('/api/adventures/categories'),

  // Get criteria types
  getCriteriaTypes: () =>
    ApiService.get<CriteriaTypeDto[]>('/api/adventures/criteriatypes'),

  // Get node types
  getNodeTypes: () =>
    ApiService.get<NodeTypeDto[]>('/api/adventures/nodetypes'),

  // Image uploads
  uploadThumbnail: (id: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return ApiService.upload<ApiResponse<{ url: string }>>(`/api/adventures/${id}/thumbnail`, formData)
  },

  uploadCover: (id: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return ApiService.upload<ApiResponse<{ url: string }>>(`/api/adventures/${id}/cover`, formData)
  },

  // Criteria - Get available criteria types for a template
  // TODO: Replace with actual API call when backend is ready
  getAvailableCriteria: async (_templateType: number) => {
    // Placeholder: Return all available criteria types until API is ready
    // In the future, this will call: /api/templates/${templateType}/available-criteria
    const { AVAILABLE_CRITERIA_TYPES } = await import('@features/adventures/types/adventure-editor.types')
    return { criteriaTypes: AVAILABLE_CRITERIA_TYPES }
  },

  // ========================
  // Display Criteria
  // ========================

  // Get display criteria for an adventure (returns groups with nested criteria)
  getDisplayCriteria: (id: string) =>
    ApiService.get<CriteriaGroupDto[]>(`/api/adventures/${id}/display-criteria`),

  // Add a display criterion
  addDisplayCriterion: (id: string, data: AddCriterionRequest) =>
    ApiService.post<CriterionResponse>(`/api/adventures/${id}/display-criteria/add`, data),

  // Update a display criterion
  updateDisplayCriterion: (id: string, criterionId: string, data: UpdateCriterionRequest) =>
    ApiService.put<CriterionResponse>(`/api/adventures/${id}/display-criteria/${criterionId}`, data),

  // Delete a display criterion
  deleteDisplayCriterion: (id: string, criterionId: string) =>
    ApiService.delete<boolean>(`/api/adventures/${id}/display-criteria/${criterionId}`),

  // Display Criteria Logic Groups
  createDisplayCriteriaGroup: (id: string, data: CriteriaGroupCreateRequest) =>
    ApiService.post<CriteriaGroupResponse>(`/api/adventures/${id}/display-criteria/groups`, data),

  updateDisplayCriteriaGroup: (id: string, groupId: string, data: CriteriaGroupUpdateRequest) =>
    ApiService.put<CriteriaGroupResponse>(`/api/adventures/${id}/display-criteria/groups/${groupId}`, data),

  deleteDisplayCriteriaGroup: (id: string, groupId: string) =>
    ApiService.delete<boolean>(`/api/adventures/${id}/display-criteria/groups/${groupId}`),

  // ========================
  // Join Criteria
  // ========================

  // Get join criteria for an adventure (returns groups with nested criteria)
  getJoinCriteria: (id: string) =>
    ApiService.get<CriteriaGroupDto[]>(`/api/adventures/${id}/join-criteria`),

  // Add a join criterion
  addJoinCriterion: (id: string, data: AddCriterionRequest) =>
    ApiService.post<CriterionResponse>(`/api/adventures/${id}/join-criteria/add`, data),

  // Update a join criterion
  updateJoinCriterion: (id: string, criterionId: string, data: UpdateCriterionRequest) =>
    ApiService.put<CriterionResponse>(`/api/adventures/${id}/join-criteria/${criterionId}`, data),

  // Delete a join criterion
  deleteJoinCriterion: (id: string, criterionId: string) =>
    ApiService.delete<boolean>(`/api/adventures/${id}/join-criteria/${criterionId}`),

  // Join Criteria Logic Groups
  createJoinCriteriaGroup: (id: string, data: CriteriaGroupCreateRequest) =>
    ApiService.post<CriteriaGroupResponse>(`/api/adventures/${id}/join-criteria/groups`, data),

  updateJoinCriteriaGroup: (id: string, groupId: string, data: CriteriaGroupUpdateRequest) =>
    ApiService.put<CriteriaGroupResponse>(`/api/adventures/${id}/join-criteria/groups/${groupId}`, data),

  deleteJoinCriteriaGroup: (id: string, groupId: string) =>
    ApiService.delete<boolean>(`/api/adventures/${id}/join-criteria/groups/${groupId}`),

  // ========================
  // Use Display for Joining
  // ========================

  // Set whether to use display criteria for joining
  setUseDisplayForJoining: (id: string, useDisplayForJoining: boolean) =>
    ApiService.put<boolean>(`/api/adventures/${id}/use-display-for-joining`, { useDisplayForJoining }),

  // ========================
  // Moderator Status
  // ========================

  // Get moderator online status for an adventure
  getModeratorStatus: (id: string) =>
    ApiService.get<ModeratorStatusDto>(`/api/adventures/${id}/moderator-status`),

  // ========================
  // Adventure History
  // ========================

  // Get change history for an adventure
  getHistory: (id: string, params?: { skip?: number; take?: number }) =>
    ApiService.get<AdventureHistoryResponse>(`/api/adventures/${id}/history`, params),

  // ========================
  // Leaderboard (for Trail adventures)
  // ========================

  // Get completion time leaderboard for an adventure
  getLeaderboard: (id: string, params?: LeaderboardQueryParams) =>
    ApiService.get<AdventureLeaderboardResponse>(`/api/adventures/${id}/leaderboard`, params),

  // ========================
  // GPX Export
  // ========================

  // Get routes data for GPX export (respects disclosure rules based on session progress)
  getRoutesForExport: (id: string, sessionId?: string) =>
    ApiService.get<GpxExportData>(`/api/adventures/${id}/routes/export`, sessionId ? { sessionId } : undefined),
}

// History types
export interface AdventureChangeDto {
  id: string
  adventureId: string
  fieldName: string
  oldValue: string | null
  newValue: string | null
  changedByUserId: string
  changedByUserName: string
  changedAt: string
  reason: string | null
}

export interface AdventureHistoryResponse {
  adventureId: string
  adventureTitle: string | null
  changes: AdventureChangeDto[]
  totalCount: number
  skip: number
  take: number
  hasMore: boolean
}

// GPX Export types
export interface GpxRouteExportDto {
  id: string
  label: string
  travelMode: string
  routePoints: [number, number][]  // Array of [lat, lng]
  distanceMeters?: number
  durationSeconds?: number
}

export interface GpxWaypointExportDto {
  id: string
  name: string
  description?: string
  latitude: number
  longitude: number
  type: 'start' | 'end' | 'location'
}

export interface GpxExportData {
  adventureName: string
  adventureDescription?: string
  creatorName?: string
  routes: GpxRouteExportDto[]
  waypoints: GpxWaypointExportDto[]
  exportEnabled: boolean
}
